import { useEffect, useState } from 'react';
import { api, BladApi, type ListaZlecen, type Sesja, type StatusZlecenia } from '../api';
import { data } from '../format';
import { idz } from '../router';
import { IkonaPlus } from '../components/Icons';
import { NaglowekWidoku } from '../components/Shell';
import {
  ChipFiltra,
  ChipZlecenia,
  Karta,
  Komunikat,
  Przycisk,
  PrzyciskMarki,
  Pusto,
  Tag,
  nazwaStatusuZlecenia,
} from '../components/ui';

const NA_STRONE = 30;
const STATUSY: StatusZlecenia[] = ['NOWE', 'W_TOKU', 'WSTRZYMANE', 'ZAKONCZONE'];

type Filtr = { status?: StatusZlecenia; fakt?: 'bez-przypisania' };

/**
 * Lista zleceń serwisowych.
 *
 * Filtry to stany z modelu **plus fakty wyliczane z danych** — „bez przypisania"
 * nie jest statusem w bazie, tylko pytaniem „czy ktoś to wziął" (docs/model-danych.md §5).
 * Statusu nie da się zmienić z listy: wynika z pracy serwisanta.
 *
 * Na telefonie lista jest kartami, na biurku tabelą. To nie kaprys: serwisant czyta
 * jedno zlecenie naraz i klika w nie palcem, a biuro przegląda dwadzieścia wzrokiem.
 */
export function TicketsView({ sesja, szukane = '' }: { sesja: Sesja; szukane?: string }) {
  const mozePrzyjmowac = sesja.permissions.includes('ticket.write');

  const [filtr, setFiltr] = useState<Filtr>({});
  const [strona, setStrona] = useState(0);
  const [wynik, setWynik] = useState<ListaZlecen | null>(null);
  const [dosypane, setDosypane] = useState<ListaZlecen['zlecenia']>([]);
  const [blad, setBlad] = useState<string | null>(null);

  useEffect(() => {
    setStrona(0);
    setDosypane([]);
  }, [szukane, filtr]);

  useEffect(() => {
    let aktualne = true;
    setBlad(null);

    api
      .zlecenia({ q: szukane, ...filtr, limit: NA_STRONE, offset: strona * NA_STRONE })
      .then((odpowiedz) => {
        if (!aktualne) return;
        setWynik(odpowiedz);
        setDosypane((poprzednie) =>
          strona === 0 ? odpowiedz.zlecenia : [...poprzednie, ...odpowiedz.zlecenia],
        );
      })
      .catch((error: unknown) => {
        if (aktualne) {
          setBlad(error instanceof BladApi ? error.message : 'Nie udało się wczytać listy.');
        }
      });

    return () => {
      aktualne = false;
    };
  }, [szukane, filtr, strona]);

  const podsumowanie = wynik?.podsumowanie;

  return (
    <>
      <NaglowekWidoku
        tytul="Zlecenia serwisowe"
        podtytul={
          szukane
            ? `Szukanie: ${szukane}`
            : podsumowanie
              ? `${podsumowanie.wszystkie} zleceń · ${podsumowanie.bezPrzypisania} bez przypisania`
              : undefined
        }
        akcje={
          mozePrzyjmowac ? (
            <PrzyciskMarki
              type="button"
              onClick={() => idz('/zlecenia/nowe')}
              ikona={<IkonaPlus rozmiar={16} />}
            >
              Nowe zgłoszenie
            </PrzyciskMarki>
          ) : undefined
        }
      />

      {blad && (
        <div className="mb-14">
          <Komunikat tresc={blad} />
        </div>
      )}

      <div className="mb-14 flex flex-wrap items-center gap-7">
        <ChipFiltra
          type="button"
          aktywny={!filtr.status && !filtr.fakt}
          licznik={podsumowanie?.wszystkie}
          onClick={() => setFiltr({})}
        >
          Wszystkie
        </ChipFiltra>
        {/* Jedyny filtr, który wymaga reakcji dziś — dlatego zaraz po „wszystkie". */}
        <ChipFiltra
          type="button"
          aktywny={filtr.fakt === 'bez-przypisania'}
          licznik={podsumowanie?.bezPrzypisania}
          onClick={() => setFiltr({ fakt: 'bez-przypisania' })}
        >
          Bez przypisania
        </ChipFiltra>
        {STATUSY.map((kod) => (
          <ChipFiltra
            key={kod}
            type="button"
            aktywny={filtr.status === kod}
            licznik={podsumowanie?.wgStatusu[kod] ?? 0}
            onClick={() => setFiltr({ status: kod })}
          >
            {nazwaStatusuZlecenia(kod)}
          </ChipFiltra>
        ))}
        <span className="t-small ml-auto hidden text-n-500 xl:block">
          Statusu nie da się zmienić z listy — wynika z pracy serwisanta.
        </span>
      </div>

      <Karta wypelnienie={false}>
        {wynik === null && <Pusto tresc="Wczytywanie…" />}

        {wynik !== null && dosypane.length === 0 && (
          <Pusto
            tresc={
              szukane || filtr.status || filtr.fakt
                ? 'Nic nie pasuje do tych warunków.'
                : 'Nic tu nie ma. Dobrze.'
            }
          />
        )}

        {/* Telefon: karty. Jedno zlecenie naraz, cel dotykowy na całą szerokość. */}
        {dosypane.length > 0 && (
          <div className="lg:hidden">
            {dosypane.map((zlecenie) => (
              <button
                key={zlecenie.id}
                type="button"
                onClick={() => idz(`/zlecenia/${zlecenie.id}`)}
                className="flex w-full flex-col items-stretch gap-5 border-0 border-b border-n-100 bg-surface px-14 py-13 text-left last:border-b-0"
              >
                <span className="flex flex-wrap items-center gap-9">
                  <span className="t-mono-data text-n-900">{zlecenie.numer}</span>
                  <ChipZlecenia status={zlecenie.status} />
                  {zlecenie.priorytet !== 'normalny' && <Tag>{zlecenie.priorytet}</Tag>}
                  {!zlecenie.technician && <Tag>bez przypisania</Tag>}
                </span>
                <span className="t-body-strong text-n-900">{zlecenie.customer.nazwa}</span>
                <span className="t-small text-n-600">
                  {zlecenie.device.producent} {zlecenie.device.model} · nr boczny{' '}
                  {zlecenie.device.sideNumber}
                </span>
                <span className="t-small line-clamp-2 text-n-700">{zlecenie.opisUsterki}</span>
                {zlecenie.wstrzymaniePowod && (
                  <span className="t-small text-[var(--status-hold-text)]">
                    {zlecenie.wstrzymaniePowod}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Biurko: tabela. Dwadzieścia zleceń przegląda się wzrokiem, nie kciukiem. */}
        {dosypane.length > 0 && (
          <div className="hidden overflow-x-auto lg:block">
            <table className="tab">
              <thead>
                <tr>
                  <th>Numer</th>
                  <th>Klient / maszyna</th>
                  <th>Usterka</th>
                  <th>Tryb</th>
                  <th>Serwisant</th>
                  <th>Termin</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {dosypane.map((zlecenie) => (
                  <tr
                    key={zlecenie.id}
                    data-klikalny=""
                    tabIndex={0}
                    role="link"
                    onClick={() => idz(`/zlecenia/${zlecenie.id}`)}
                    onKeyDown={(zdarzenie) => {
                      if (zdarzenie.key === 'Enter' || zdarzenie.key === ' ') {
                        zdarzenie.preventDefault();
                        idz(`/zlecenia/${zlecenie.id}`);
                      }
                    }}
                  >
                    <td>
                      <div className="t-mono-data whitespace-nowrap text-n-900">
                        {zlecenie.numer}
                      </div>
                      <div className="t-small text-n-600">{data(zlecenie.dataZgloszenia)}</div>
                    </td>
                    <td>
                      <div className="t-body-strong text-n-900">{zlecenie.customer.nazwa}</div>
                      <div className="t-small text-n-600">
                        {zlecenie.device.producent} {zlecenie.device.model} ·{' '}
                        {zlecenie.device.sideNumber}
                      </div>
                    </td>
                    <td className="t-small max-w-[280px] text-n-700">
                      <span className="line-clamp-2">{zlecenie.opisUsterki}</span>
                      {zlecenie.wstrzymaniePowod && (
                        <span className="t-small mt-4 block text-[var(--status-hold-text)]">
                          {zlecenie.wstrzymaniePowod}
                        </span>
                      )}
                    </td>
                    <td className="t-small whitespace-nowrap text-n-600">
                      {zlecenie.tryb === 'WYJAZD' ? 'wyjazd' : 'warsztat'}
                    </td>
                    <td className="t-small whitespace-nowrap">
                      {zlecenie.technician ? (
                        <span className="text-n-700">{zlecenie.technician.imieNazwisko}</span>
                      ) : (
                        <span className="text-[var(--status-failure-text)]">bez przypisania</span>
                      )}
                    </td>
                    <td className="t-mono-data whitespace-nowrap text-n-600">
                      {data(zlecenie.termin) || '—'}
                    </td>
                    <td>
                      <ChipZlecenia status={zlecenie.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {wynik !== null && dosypane.length < wynik.ile && (
          <div className="border-t border-n-100 px-14 py-10">
            <Przycisk type="button" onClick={() => setStrona((s) => s + 1)}>
              Pokaż kolejne ({wynik.ile - dosypane.length})
            </Przycisk>
          </div>
        )}
      </Karta>
    </>
  );
}
